// web/src/routes/schedule/index.jsx
// The Schedule board — one place for everything happening at the barn.
//
// Four views over the same anchor date: Day (what you work from), Week (the
// default — tasks and lessons side by side across seven days), Month (the
// "when is that clinic again" view) and Riders (the rider × days grid,
// unchanged). View, date and the person filter all live in the URL, so any
// particular week — or "Ava's next month" — is a link.
//
// Day, Week and Month read /schedule, which returns tasks + lessons for a range
// in one call. Riders keeps reading /lessons through the existing grid — it
// needs the rider roster too, and nothing is gained by routing it through here.

import { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import AddTaskModal from '../../components/board/AddTaskModal';
import LessonEditor from '../../components/lessons/LessonEditor';
import WeekView from '../../components/schedule/WeekView';
import DayView from '../../components/schedule/DayView';
import MonthView from '../../components/schedule/MonthView';
import BoardFilters from '../../components/schedule/BoardFilters';
import ItemModal from '../../components/schedule/ItemModal';
import WeeklyBoard from '../board/Weekly';
import {
  addDaysISO,
  addMonthsISO,
  weekStart,
  monthGrid,
  personKey,
  itemsForDate,
  itemRoles,
  activityKey,
  activityLabel,
  ROLE_GROUPS,
  taskMatchesPerson,
  lessonMatchesPerson,
  formatDay,
  formatRange,
} from '../../lib/schedule';

const VIEW_ROLES = ['owner', 'manager', 'trainer', 'groom'];
const TASK_ROLES = ['owner', 'manager']; // POST /tasks
const LESSON_ROLES = ['owner', 'manager', 'trainer']; // POST /lessons

const VIEWS = [
  ['day', 'Day'],
  ['week', 'Week'],
  ['month', 'Month'],
  ['riders', 'Riders'],
];

export default function ScheduleBoard() {
  const qc = useQueryClient();
  const { activeRole } = useAuth();
  const canView = VIEW_ROLES.includes(activeRole);
  const canAddTask = TASK_ROLES.includes(activeRole);
  const canEditLesson = LESSON_ROLES.includes(activeRole);

  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const view = VIEWS.some(([v]) => v === searchParams.get('view'))
    ? searchParams.get('view')
    : 'week';
  // null = "whatever the server calls today"; resolved on the first response.
  const anchor = searchParams.get('date');
  // "u:<userId>" / "p:<personId>", or null for everyone. See lib/schedule.
  const who = searchParams.get('who');
  // The role bucket the who-chips are expanded under, and the activity chip.
  // Both in the URL beside `who`, so a sliced week stays a link.
  const role = searchParams.get('role');
  const activity = searchParams.get('act');

  const [adding, setAdding] = useState(null); // { date } → task form
  const [lessonEditor, setLessonEditor] = useState(null); // { lesson, date }
  const [chooser, setChooser] = useState(null); // { date } → task-or-lesson
  const [detail, setDetail] = useState(null); // the board item being read

  function setParams(next) {
    const params = new URLSearchParams(searchParams);
    for (const [k, v] of Object.entries(next)) {
      if (v === null) params.delete(k);
      else params.set(k, v);
    }
    setSearchParams(params, { replace: true });
  }

  // Each view asks for exactly the span it draws: one day, one Mon–Sun week, or
  // the whole-week grid a month calendar needs (so its edge days aren't blank).
  const grid = anchor && view === 'month' ? monthGrid(anchor) : null;
  const span = view === 'day' ? 'day' : view === 'month' ? 'month' : 'week';
  // The week is the seven days FROM the anchor, not the calendar week around
  // it. A barn's question is "what's coming", and on a Thursday a Mon–Sun grid
  // spends four of its seven columns on days that already happened.
  const from = !anchor ? null : grid ? grid.from : anchor;
  const to = !from ? null : grid ? grid.to : view === 'day' ? from : addDaysISO(from, 6);

  const scheduleQ = useQuery({
    queryKey: ['schedule', span, from ?? 'current'],
    queryFn: () => api.get(from ? `/schedule?from=${from}&to=${to}` : '/schedule'),
    // Runs in the Riders view too, which doesn't render its results: it's what
    // keeps the barn's "today" resolved in every view, and it leaves Day/Week
    // warm so switching tabs doesn't flash a spinner.
    enabled: canView,
    retry: false,
  });
  // Riders for the lesson editor's picker — only fetched once someone can edit.
  const peopleQ = useQuery({
    queryKey: ['people'],
    queryFn: () => api.get('/people'),
    enabled: canView && canEditLesson,
  });
  const horsesQ = useQuery({
    queryKey: ['horses'],
    queryFn: () => api.get('/horses'),
    enabled: canView && canEditLesson,
  });
  // Roles, so the who-chips can tell a groom from a manager. /schedule carries
  // names but not roles, and "who are the grooms" is the question the top row
  // exists to answer.
  const teamQ = useQuery({
    queryKey: ['team'],
    queryFn: () => api.get('/team'),
    // Owner and manager only. Everyone else gets the derived buckets above
    // rather than a pointless 403 on every page load.
    enabled: activeRole === 'owner' || activeRole === 'manager',
    retry: false,
  });

  // The barn's today, latched from the first response that carries it: the
  // Riders view doesn't call /schedule, so without this the "Today" affordance
  // would forget the date the moment you switched to it.
  const [today, setToday] = useState(null);
  useEffect(() => {
    if (scheduleQ.data?.today) setToday(scheduleQ.data.today);
  }, [scheduleQ.data]);

  // Lock the URL onto a concrete date once the server tells us today, so the
  // steppers below are pure ISO math rather than guesses about the barn's clock.
  useEffect(() => {
    if (!anchor && today) setParams({ date: today });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [anchor, today]);

  const removeLesson = useMutation({
    mutationFn: (id) => api.del(`/lessons/${id}`),
    onSuccess: () => {
      setDetail(null);
      qc.invalidateQueries({ queryKey: ['schedule'] });
      qc.invalidateQueries({ queryKey: ['lessons'] });
      qc.invalidateQueries({ queryKey: ['dashboard'] });
    },
  });

  const setTaskStatus = useMutation({
    mutationFn: ({ id, status }) => api.patch(`/tasks/${id}`, { status }),
    onSuccess: () => {
      setDetail(null);
      qc.invalidateQueries({ queryKey: ['schedule'] });
      qc.invalidateQueries({ queryKey: ['tasks'] });
      qc.invalidateQueries({ queryKey: ['dashboard'] });
    },
  });

  const toggleTask = useMutation({
    mutationFn: ({ id, done }) =>
      done ? api.patch(`/tasks/${id}`, { status: 'pending' }) : api.post(`/tasks/${id}/complete`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['schedule'] });
      qc.invalidateQueries({ queryKey: ['tasks'] });
      qc.invalidateQueries({ queryKey: ['dashboard'] });
    },
  });

  if (!canView) {
    return (
      <div className="max-w-7xl mx-auto p-4">
        <div className="card p-8 text-center text-ink-500">The schedule is for barn staff.</div>
      </div>
    );
  }

  const viewDate = anchor ?? today;
  // Rolling: today on the left, the next six after it. The steppers move the
  // window a week at a time, so last week is still one tap back.
  const days = viewDate ? Array.from({ length: 7 }, (_, i) => addDaysISO(viewDate, i)) : [];
  // The Riders grid is a different thing and still reads Mon–Sun — it's a
  // roster laid out against a calendar week, not a queue of what's next.
  const weekFrom = viewDate ? weekStart(viewDate) : null;

  // Filtering runs client-side: /schedule already returned the whole range, so
  // narrowing is a predicate rather than another round trip — and the counts on
  // the chips need the unfiltered set anyway.
  const allTasks = scheduleQ.data?.tasks ?? [];
  const allLessons = scheduleQ.data?.lessons ?? [];
  const allServices = scheduleQ.data?.services ?? [];

  const roleByUserId = Object.fromEntries(
    (teamQ.data?.members ?? []).map((m) => [m.userId, m.role]),
  );

  // Every item in the visible range, unfiltered — the set the chips count.
  const rangeDays = view === 'month' ? (grid?.weeks ?? []).flat() : view === 'day' ? [viewDate] : days;
  const allItems = rangeDays
    .filter(Boolean)
    .flatMap((d) => itemsForDate(d, allTasks, allLessons, allServices));

  // Roles that actually have something on, with their counts.
  const roleCounts = new Map();
  const membersByRole = new Map();
  for (const item of allItems) {
    for (const r of itemRoles(item, roleByUserId)) {
      roleCounts.set(r, (roleCounts.get(r) ?? 0) + 1);
      const bucket = membersByRole.get(r) ?? new Map();
      for (const p of item.people ?? []) {
        // The same rule itemRoles uses, applied one person at a time, so a
        // chip only lands in the bucket its owner was counted into.
        if (!itemRoles({ ...item, people: [p] }, roleByUserId).has(r)) continue;
        const seen = bucket.get(p.ref) ?? { ref: p.ref, name: p.name, count: 0 };
        seen.count += 1;
        bucket.set(p.ref, seen);
      }
      membersByRole.set(r, bucket);
    }
  }
  const roles = ROLE_GROUPS.filter(([id]) => roleCounts.get(id)).map(([id, label]) => ({
    id,
    label,
    count: roleCounts.get(id),
  }));
  const members = [...(membersByRole.get(role)?.values() ?? [])].sort((a, b) =>
    a.name.localeCompare(b.name),
  );

  // Activities, most common first — a barn's week is mostly turnout, and the
  // chip you reach for should be near the front.
  const actMap = new Map();
  for (const item of allItems) {
    const key = activityKey(item);
    const seen = actMap.get(key) ?? { key, label: activityLabel(item), count: 0 };
    seen.count += 1;
    actMap.set(key, seen);
  }
  const activities = [...actMap.values()].sort(
    (a, b) => b.count - a.count || a.label.localeCompare(b.label),
  );

  /** Does an item survive the role / person / activity chips? */
  function keep(item) {
    if (role && !itemRoles(item, roleByUserId).has(role)) return false;
    if (who && !(item.people ?? []).some((p) => p.ref === who)) return false;
    if (activity && activityKey(item) !== activity) return false;
    return true;
  }
  const shownCount = allItems.filter(keep).length;

  // The views still take raw rows, so the chips are translated back into the
  // predicates those rows understand.
  const keepsTask = (t) => keep(itemsForDate(t.date, [t], [], [])[0]);
  const keepsLesson = (l) => keep(itemsForDate(l.date, [], [l], [])[0]);
  const keepsService = (v) => keep(itemsForDate(v.date, [], [], [v])[0]);

  const tasks = allTasks.filter((t) => taskMatchesPerson(t, who) && keepsTask(t));
  const lessons = allLessons.filter((l) => lessonMatchesPerson(l, who) && keepsLesson(l));
  const services = allServices.filter(keepsService);

  const unit = view === 'month' ? 'month' : view === 'day' ? 'day' : 'week';
  function stepBy(dir) {
    if (!viewDate) return;
    const next =
      view === 'month'
        ? addMonthsISO(viewDate, dir)
        : addDaysISO(viewDate, dir * (view === 'day' ? 1 : 7));
    setParams({ date: next });
  }

  // A click reads the row. It used to tick a task off on the spot, which is
  // right on the Daily Board — somebody working a list with a phone in one
  // hand — and wrong here, where the same click is how you find out what the
  // row is. Marking done is a button in the modal now.
  function openItem(item) {
    setDetail(item);
  }


  return (
    <div className="max-w-7xl mx-auto p-4">
      <header className="mb-4 space-y-3">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h1 className="text-2xl font-semibold">Schedule</h1>
          <ViewTabs view={view} onChange={(v) => setParams({ view: v })} />
        </div>

        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <StepButton label={`Previous ${unit}`} dir="prev" onClick={() => stepBy(-1)} />
            <span className="text-base font-semibold min-w-[12rem] text-center">
              {view === 'day'
                ? formatDay(viewDate, { weekday: 'long', month: 'short', day: 'numeric' })
                : view === 'month'
                  ? formatDay(viewDate, { month: 'long', year: 'numeric' })
                  : formatRange(days[0], days[6])}
            </span>
            <StepButton label={`Next ${unit}`} dir="next" onClick={() => stepBy(1)} />
            {viewDate !== today && (
              <button onClick={() => setParams({ date: null })} className="text-xs text-ink-500 underline ml-1">
                Today
              </button>
            )}
          </div>

          <div className="flex items-center gap-2">
            {canAddTask && (
              <button onClick={() => setAdding({ date: viewDate })} className="btn-secondary text-sm">
                + Task
              </button>
            )}
            {canEditLesson && (
              <button
                onClick={() => setLessonEditor({ lesson: null, date: viewDate })}
                className="btn-secondary text-sm"
              >
                + Lesson
              </button>
            )}
          </div>
        </div>
      </header>

      {view !== 'riders' && allItems.length > 0 && (
        <div className="mb-4">
          <BoardFilters
            roles={roles}
            role={role}
            // Changing role drops the person with it: a chip for somebody who
            // isn't in the role you're now looking at would filter to nothing.
            onRole={(r) => setParams({ role: r === role ? null : r, who: null })}
            members={members}
            who={who}
            onWho={(w) => setParams({ who: w })}
            activities={activities}
            activity={activity}
            onActivity={(a) => setParams({ act: a })}
            showing={shownCount}
            total={allItems.length}
          />
        </div>
      )}

      {detail && (
        <ItemModal
          item={detail}
          canEditLesson={canEditLesson}
          canToggleTask={canAddTask || activeRole === 'trainer' || activeRole === 'groom'}
          busy={toggleTask.isPending || removeLesson.isPending || setTaskStatus.isPending}
          onToggleDone={() => {
            toggleTask.mutate(
              { id: detail.id, done: detail.done },
              { onSuccess: () => setDetail(null) },
            );
          }}
          onCancelTask={() => setTaskStatus.mutate({ id: detail.id, status: 'cancelled' })}
          onEdit={() => {
            const item = detail;
            setDetail(null);
            setLessonEditor({ lesson: item.raw, date: item.date });
          }}
          onDelete={() => removeLesson.mutate(detail.id)}
          onOpenHorse={() => {
            if (detail.horseId) navigate(`/horses/${detail.horseId}`);
          }}
          onClose={() => setDetail(null)}
        />
      )}

      {view === 'riders' ? (
        <WeeklyBoard
          embedded
          weekFrom={weekFrom}
          onWeekChange={(d) => setParams({ date: d })}
          // A rider's name in the grid is the shortest route to their whole
          // week — tasks included, which the grid itself can't show.
          onShowRider={(p) => setParams({ view: 'week', who: personKey('p', p.id) })}
        />
      ) : scheduleQ.isError ? (
        <p className="text-red-600 text-sm">Couldn’t load the schedule. Try again.</p>
      ) : scheduleQ.isLoading || !viewDate ? (
        <div className="card p-8 text-center text-ink-400">Loading…</div>
      ) : view === 'day' ? (
        <DayView
          date={viewDate}
          tasks={tasks}
          lessons={lessons}
          services={services}
          canAdd={canAddTask}
          busy={toggleTask.isPending}
          onAdd={() => setAdding({ date: viewDate })}
          onOpenItem={openItem}
          onFilterPerson={(ref) => setParams({ who: ref })}
        />
      ) : view === 'month' ? (
        <MonthView
          weeks={grid?.weeks ?? []}
          month={viewDate.slice(0, 7)}
          today={today}
          tasks={tasks}
          lessons={lessons}
          services={services}
          onOpenDay={(date) => setParams({ view: 'day', date })}
        />
      ) : (
        <WeekView
          days={days}
          today={today}
          tasks={tasks}
          lessons={lessons}
          services={services}
          // A column header opens that day. It replaced the "Today's board"
          // link, which only ever went to one day and was the wrong one six
          // days out of seven.
          onOpenDay={(date) => setParams({ view: 'day', date })}
          canAdd={canAddTask || canEditLesson}
          busy={toggleTask.isPending}
          onAddOnDay={(date) => {
            // Only one thing you're allowed to add? Skip straight to it.
            if (canAddTask && canEditLesson) setChooser({ date });
            else if (canAddTask) setAdding({ date });
            else setLessonEditor({ lesson: null, date });
          }}
          onOpenItem={openItem}
        />
      )}

      {chooser && (
        <AddChooser
          date={chooser.date}
          onTask={() => {
            setAdding({ date: chooser.date });
            setChooser(null);
          }}
          onLesson={() => {
            setLessonEditor({ lesson: null, date: chooser.date });
            setChooser(null);
          }}
          onClose={() => setChooser(null)}
        />
      )}

      {adding && <AddTaskModal date={adding.date} onClose={() => setAdding(null)} />}

      {lessonEditor && (
        <LessonEditor
          lesson={lessonEditor.lesson}
          date={lessonEditor.date}
          riderPersonId={lessonEditor.lesson?.riderPersonId}
          riderName={lessonEditor.lesson?.riderName}
          riders={peopleQ.data?.people ?? []}
          horses={horsesQ.data?.horses ?? []}
          onClose={() => setLessonEditor(null)}
        />
      )}
    </div>
  );
}

function ViewTabs({ view, onChange }) {
  return (
    <div className="inline-flex rounded-lg border border-ink-200 bg-white p-0.5" role="tablist">
      {VIEWS.map(([id, label]) => (
        <button
          key={id}
          role="tab"
          aria-selected={view === id}
          onClick={() => onChange(id)}
          className={
            'px-3 py-1.5 text-sm rounded-md transition ' +
            (view === id ? 'bg-hunter text-white' : 'text-ink-600 hover:bg-ink-50')
          }
        >
          {label}
        </button>
      ))}
    </div>
  );
}

// Built from whoever appears in the loaded range, so it works for every role
// (grooms can't read /team) and never offers a name with nothing behind it.
// The trade: someone with an empty week isn't listed that week.

/**
 * Stepping a week.
 *
 * These are the most-used control on the page and they read as the least: a
 * hairline box around a typographic ‹, which is a punctuation mark rather
 * than an arrow and renders barely a pixel wide. A real chevron, a border you
 * can see, and a target big enough for a thumb.
 */
function StepButton({ label, onClick, dir }) {
  return (
    <button
      onClick={onClick}
      aria-label={label}
      title={label}
      className={
        'w-10 h-10 rounded-lg border border-ink-300 bg-white text-ink-700 shadow-sm ' +
        'inline-flex items-center justify-center transition-colors ' +
        'hover:border-hunter hover:text-hunter active:scale-95'
      }
    >
      <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
        <path
          d={dir === 'prev' ? 'M12.5 4.5 7 10l5.5 5.5' : 'M7.5 4.5 13 10l-5.5 5.5'}
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    </button>
  );
}

// Tapping "+ Add" on a day can mean either thing, and guessing wrong costs a
// dismissed modal — so ask, in two taps' worth of sheet.
function AddChooser({ date, onTask, onLesson, onClose }) {
  return (
    <div
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/30 sm:p-4"
      onClick={onClose}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Add to this day"
        className="card w-full sm:max-w-xs p-5 rounded-b-none sm:rounded-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <p className="text-sm text-ink-500 mb-3">
          {formatDay(date, { weekday: 'long', month: 'short', day: 'numeric' })}
        </p>
        <div className="space-y-2">
          <button onClick={onTask} className="btn-secondary w-full">Add Task</button>
          <button onClick={onLesson} className="btn-secondary w-full">Add Lesson</button>
        </div>
        <button onClick={onClose} className="mt-3 w-full text-sm text-ink-500 hover:text-ink-800">
          Cancel
        </button>
      </div>
    </div>
  );
}
